import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";

function fmtDur(sec) {
  if (!sec) return "0:00";
  const m = Math.floor(sec / 60);
  const s = String(sec % 60).padStart(2, "0");
  return `${m}:${s}`;
}

const KPI = ({ label, value, sub }) => (
  <div className="border border-cream2 p-6 bg-cream" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="text-[10px] uppercase tracking-[0.24em] text-navy/50">{label}</div>
    <div className="font-display text-5xl text-navy mt-3 leading-none">{value}</div>
    {sub && <div className="mt-2 text-xs text-navy/60">{sub}</div>}
  </div>
);

export default function StatsPanel({ island, reloadKey }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats", { params: { island_slug: island, days: 30 } })
      .then((r) => setStats(r.data))
      .catch(() => setStats(null));
  }, [island, reloadKey]);

  if (!stats) return <div className="text-navy/50">Loading…</div>;

  return (
    <div className="space-y-10" data-testid="stats-panel">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPI label="Total leads" value={stats.total} sub="Last 30 days" />
        <KPI label="Answered" value={stats.answered} sub={`${stats.answered_rate}% answer rate`} />
        <KPI label="Missed / VM" value={stats.missed} sub="Missed calls + voicemails" />
        <KPI label="Avg call" value={fmtDur(stats.avg_duration_seconds)} sub="For answered calls" />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPI label="Completed rides" value={stats.completed_rides ?? 0} sub={`${stats.conversion_rate ?? 0}% conversion`} />
        <KPI label="No-shows" value={stats.no_shows ?? 0} sub="Marked no-show by ops" />
        <KPI label="Cancelled" value={stats.cancelled ?? 0} sub="Rider or operator cancel" />
        <KPI label="Outcome pending" value={stats.outcome_pending ?? 0} sub="Awaiting confirmation" />
      </div>

      <div className="border border-cream2 bg-cream p-6">
        <div className="text-[10px] uppercase tracking-[0.24em] text-navy/50 mb-4">Leads per day</div>
        <div className="h-64" data-testid="stats-chart-day">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.by_day} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#0B1F2F", opacity: 0.6 }} tickLine={false} axisLine={{ stroke: "#D5C8B8" }} />
              <YAxis tick={{ fontSize: 10, fill: "#0B1F2F", opacity: 0.6 }} tickLine={false} axisLine={{ stroke: "#D5C8B8" }} allowDecimals={false} />
              <Tooltip cursor={{ fill: "#D5C8B855" }} contentStyle={{ background: "#EEE7DE", border: "1px solid #D5C8B8", borderRadius: 0, fontSize: 12 }} />
              <Bar dataKey="count" fill="#B8892A" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="border border-cream2 bg-cream p-6">
        <div className="text-[10px] uppercase tracking-[0.24em] text-navy/50 mb-4">Leads per operator</div>
        {stats.by_operator.length === 0 ? (
          <div className="text-navy/50 py-6">No operator data yet.</div>
        ) : (
          <div className="space-y-3">
            {stats.by_operator.map((r, i) => {
              const max = stats.by_operator[0]?.count || 1;
              const pct = Math.round((r.count / max) * 100);
              return (
                <div key={i} data-testid={`op-stat-${i}`}>
                  <div className="flex justify-between text-sm text-navy mb-1">
                    <span>{r.operator_name}</span>
                    <span className="font-mono">{r.count}</span>
                  </div>
                  <div className="h-2 bg-cream2 relative">
                    <div className="absolute inset-y-0 left-0 bg-teal" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
